<template>
    <v-dialog v-model="dialogVisible" max-width="600" persistent scrollable>
        <v-card class="overflow-hidden" style="border-radius: 8px">
            <v-card-title class="text-h6">
                <small>Edit Profile</small>
                <v-spacer></v-spacer>
                <v-icon @click="closeDialog">mdi-close</v-icon>
            </v-card-title>
            <hr />
            <v-card-text class="py-4">
                <!-- Profile Picture Preview -->
                <div class="text-center mb-4" v-if="editProfile.profile_picture">
                    <v-avatar color="primary" size="80" class="mb-2">
                        <v-img :src="getProfilePictureUrl(editProfile.profile_picture)"></v-img>
                    </v-avatar>
                </div>

                <v-row>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.first_name" label="First Name" outlined dense
                            prepend-inner-icon="mdi-account" v-model="editProfile.first_name" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.last_name" label="Last Name" outlined dense
                            v-model="editProfile.last_name" />
                    </v-col>
                    <v-col cols="12">
                        <v-text-field :placeholder="user.name" label="Display Name" outlined dense
                            v-model="editProfile.name" />
                    </v-col>
                    <v-col cols="12">
                        <v-text-field :placeholder="user.email" label="Email" outlined dense
                            prepend-inner-icon="mdi-email" v-model="editProfile.email" />
                    </v-col>
                    <v-col cols="12">
                        <v-text-field :placeholder="user.secondary_email" label="Secondary Email" outlined dense
                            prepend-inner-icon="mdi-email-outline" v-model="editProfile.secondary_email" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.phone" label="Phone" outlined dense
                            prepend-inner-icon="mdi-phone" v-model="editProfile.phone" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.position_name" label="Position" outlined dense
                            prepend-inner-icon="mdi-briefcase" v-model="editProfile.position_name" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.location" label="Location" outlined dense
                            prepend-inner-icon="mdi-map-marker" v-model="editProfile.location" />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field :placeholder="user.joined_date" label="Joined Date" outlined dense type="date"
                            prepend-inner-icon="mdi-calendar" v-model="editProfile.joined_date" />
                    </v-col>
                    <v-col cols="12" v-if="canEditSkillsAndAccomplishments">
                        <!-- Skills Section -->
                        <v-text-field v-model="skillInput" label="Add Skill" outlined dense
                            prepend-inner-icon="mdi-star" @keyup.enter="addSkill" hint="Press Enter to add skill"
                            persistent-hint></v-text-field>

                        <div v-if="editProfile.skills && editProfile.skills.length > 0" class="mt-2">
                            <v-chip v-for="(skill, index) in editProfile.skills" :key="index" small close
                                @click:close="removeSkill(index)" class="mr-2 mb-2" color="primary" outlined>
                                {{ skill }}
                            </v-chip>
                        </div>
                    </v-col>
                    <v-col cols="12" v-else>
                        <!-- Read-only Skills Section -->
                        <div class="mb-4">
                            <label class="v-label theme--light">Skills</label>
                            <div class="mt-2">
                                <div v-if="editProfile.skills && editProfile.skills.length > 0">
                                    <v-chip v-for="(skill, index) in editProfile.skills" :key="index" small
                                        class="mr-2 mb-2" color="primary" outlined disabled>
                                        {{ skill }}
                                    </v-chip>
                                </div>
                                <div v-else class="text-body-2 text--secondary">
                                    No skills listed
                                </div>
                            </div>
                            <div class="text-caption text--secondary mt-1">
                                Skills can only be modified by administrators
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" v-if="canEditSkillsAndAccomplishments">
                        <!-- Accomplishments Section -->
                        <v-text-field v-model="accomplishmentInput" label="Add Accomplishment" outlined dense
                            prepend-inner-icon="mdi-trophy" @keyup.enter="addAccomplishment"
                            hint="Press Enter to add accomplishment" persistent-hint></v-text-field>

                        <div v-if="editProfile.accomplishments && editProfile.accomplishments.length > 0" class="mt-2">
                            <v-chip v-for="(accomplishment, index) in editProfile.accomplishments" :key="index" small
                                close @click:close="removeAccomplishment(index)" class="mr-2 mb-2" color="success"
                                outlined>
                                {{ accomplishment }}
                            </v-chip>
                        </div>
                    </v-col>
                    <v-col cols="12" v-else>
                        <!-- Read-only Accomplishments Section -->
                        <div class="mb-4">
                            <label class="v-label theme--light">Accomplishments</label>
                            <div class="mt-2">
                                <div v-if="editProfile.accomplishments && editProfile.accomplishments.length > 0">
                                    <v-chip v-for="(accomplishment, index) in editProfile.accomplishments" :key="index"
                                        small class="mr-2 mb-2" color="success" outlined disabled>
                                        {{ accomplishment }}
                                    </v-chip>
                                </div>
                                <div v-else class="text-body-2 text--secondary">
                                    No accomplishments listed
                                </div>
                            </div>
                            <div class="text-caption text--secondary mt-1">
                                Accomplishments can only be modified by administrators
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12">
                        <v-file-input v-model="profilePictureFile" label="Profile Picture" outlined dense
                            accept="image/*" prepend-icon="" prepend-inner-icon="mdi-camera"
                            @change="uploadProfilePicture" :loading="uploading" hint="Upload an image file"
                            persistent-hint show-size></v-file-input>
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="pa-4">
                <v-spacer></v-spacer>
                <v-btn outlined class="px-6 py-2" @click.stop="closeDialog">Cancel</v-btn>
                <v-btn color="primary" elevation="2" class="px-6 py-2" @click.stop="saveProfile" :loading="saving">
                    Update Profile
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from "vuex";

export default {
    name: "ProfileEditDialog",
    props: {
        value: {
            type: Boolean,
            default: false
        },
        user: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            editProfile: {
                name: "",
                first_name: "",
                last_name: "",
                email: "",
                secondary_email: "",
                phone: "",
                position_name: "",
                location: "",
                joined_date: "",
                profile_picture: "",
                skills: [],
                accomplishments: [],
                role_id: 0,
            },
            skillInput: '',
            accomplishmentInput: '',
            profilePictureFile: null,
            uploading: false,
            saving: false,
        };
    },
    computed: {
        ...mapGetters({
            currentUser: 'auth/get_user',
        }),
        dialogVisible: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        apiUrl() {
            return process.env.VUE_APP_API_URL || '';
        },
        canEditSkillsAndAccomplishments() {
            // Only Super Admin (1) or Admin (2) can edit skills and accomplishments
            return this.currentUser && [1, 2].includes(this.currentUser.role_id);
        }
    },
    watch: {
        value(newVal) {
            if (newVal) {
                this.initializeProfile();
            }
        }
    },
    methods: {
        initializeProfile() {
            this.editProfile = {
                name: this.currentUser.name || "",
                first_name: this.user.first_name || "",
                last_name: this.user.last_name || "",
                email: this.user.email || "",
                secondary_email: this.user.secondary_email || "",
                phone: this.user.phone || "",
                position_name: this.user.position_name || "",
                location: this.user.location || "",
                joined_date: this.user.joined_date || "",
                profile_picture: this.user.profile_picture || "",
                skills: this.user.skills ? [...this.user.skills] : [],
                accomplishments: this.user.accomplishments ? [...this.user.accomplishments] : [],
                role_id: this.currentUser.role_id || 0,
            };
        },
        closeDialog() {
            this.dialogVisible = false;
            this.profilePictureFile = null;
            this.skillInput = '';
            this.accomplishmentInput = '';
        },
        addSkill() {
            if (this.skillInput.trim() && !this.editProfile.skills.includes(this.skillInput.trim())) {
                this.editProfile.skills.push(this.skillInput.trim());
                this.skillInput = '';
            }
        },
        removeSkill(index) {
            this.editProfile.skills.splice(index, 1);
        },
        addAccomplishment() {
            if (this.accomplishmentInput.trim() && !this.editProfile.accomplishments.includes(this.accomplishmentInput.trim())) {
                this.editProfile.accomplishments.push(this.accomplishmentInput.trim());
                this.accomplishmentInput = '';
            }
        },
        removeAccomplishment(index) {
            this.editProfile.accomplishments.splice(index, 1);
        },
        getProfilePictureUrl(profilePicture) {
            if (!profilePicture) return null;

            // If it's already a full URL, return as is
            if (profilePicture.startsWith('http://') || profilePicture.startsWith('https://')) {
                return profilePicture;
            }

            // Convert old /media/ format to new /storage/ format
            let picturePath = profilePicture;
            if (picturePath.startsWith('/media/')) {
                picturePath = picturePath.replace('/media/', '/storage/');
            }

            // Otherwise, construct the full URL with the API base URL
            const apiUrl = this.apiUrl.endsWith('/') ? this.apiUrl.slice(0, -1) : this.apiUrl;
            picturePath = picturePath.startsWith('/') ? picturePath : `/${picturePath}`;
            return `${apiUrl}${picturePath}`;
        },
        async uploadProfilePicture(file) {
            if (!file) return;

            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
            if (!allowedTypes.includes(file.type)) {
                this.$toast.error('Please select a valid image file (JPEG, PNG, GIF)');
                this.profilePictureFile = null;
                return;
            }

            // Validate file size (max 5MB)
            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
            if (file.size > maxSize) {
                this.$toast.error('File size must be less than 5MB');
                this.profilePictureFile = null;
                return;
            }

            this.uploading = true;
            try {
                const formData = new FormData();
                formData.append('profile_picture', file);
                formData.append('user_id', this.user.id);

                const response = await this.$axios.post(
                    'api/users/upload_profile_picture',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                if (response.data.response) {
                    this.editProfile.profile_picture = response.data.data.profile_picture;
                    this.$toast.success('Profile picture uploaded successfully');
                } else {
                    this.$toast.error(response.data.message || 'Failed to upload profile picture');
                }
            } catch (error) {
                console.error('Error uploading profile picture:', error);
                this.$toast.error('Failed to upload profile picture');
            } finally {
                this.uploading = false;
            }
        },
        async saveProfile() {
            this.saving = true
            try {
                const payload = {
                    name: this.editProfile.name || this.editProfile.first_name + ' ' + this.editProfile.last_name,
                    first_name: this.editProfile.first_name,
                    last_name: this.editProfile.last_name,
                    email: this.editProfile.email,
                    secondary_email: this.editProfile.secondary_email,
                    phone: this.editProfile.phone,
                    position_name: this.editProfile.position_name,
                    location: this.editProfile.location,
                    joined_date: this.editProfile.joined_date,
                    // profile_picture: this.editProfile.profile_picture,
                    role_id: this.editProfile.role_id
                };

                // Only include skills and accomplishments if user has permission to edit them
                if (this.canEditSkillsAndAccomplishments) {
                    payload.skills = this.editProfile.skills
                    payload.accomplishments = this.editProfile.accomplishments
                }

                await this.$axios.post(`api/users/update_user/${this.user.id}`, payload)

                // Refresh user data
                await this.getUser(this.user.id)

                this.$emit('profile-updated')
                this.closeDialog()
            } catch (error) {
                console.error('Error updating profile:', error)
                this.$toast.error('Failed to update profile')
            } finally {
                this.saving = false
            }
        },
        // User service methods
        async getUser(id) {
            try {
                const response = await this.$axios.get(`/api/users/${id}`)
                const { data } = response
                this.$store.commit('auth/set_user', data)
            } catch (error) {
                console.log('error on fetching user information - service layer', error);
            }
        }
    }
};
</script>

<style scoped>
/* Add any specific styles if needed */
</style>